<script setup lang="ts">
import { IconSearch, IconX } from '@tabler/icons-vue'
import type { Component } from 'vue'
import { computed, getCurrentInstance, inject, onMounted, ref } from 'vue'

import { DESIGN_SYSTEM_DEFAULTS } from '../designSystemDefaults'

import BaseIcon from './BaseIcon.vue'
import BooleanSwitch from './form-input/BooleanSwitch.vue'
import CheckboxField from './form-input/CheckboxField.vue'
import CountryField from './form-input/CountryField.vue'
import DateField from './form-input/DateField.vue'
import DropzoneField from './form-input/DropzoneField.vue'
import PasswordViewableField from './form-input/PasswordViewableField.vue'
import PhoneNumberField from './form-input/PhoneNumberField'
import RadioGroupField from './form-input/RadioGroupField.vue'
import RadioGroupVerticalField from './form-input/RadioGroupVerticalField.vue'
import SelectField from './form-input/SelectField.vue'
import TextareaField from './form-input/TextareaField.vue'
import { randomIdentifier, useComputedAttrs, useComputedInput } from './form-input/helpers'
import { type FormInputExposed, type FormInputProps, type FormInputType } from './form-input/types'

const props = withDefaults(defineProps<FormInputProps>(), {
  type: 'text',
  searchable: true,
})
const emit = defineEmits(['input', 'update:modelValue', 'validation', 'click', 'reset', 'change', 'blur'])

// Label above the control for all types except those with label beside (mirrors FormInput2 / eComm prev).
const hasLabelAbove = computed(() => props.type !== 'checkbox')

const uniqueId = ref('form-id-' + randomIdentifier())
const inputEls = ref<(HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)[]>([])

const input = useComputedInput(props, emit)

if (props.type === 'select' && props.autoSelectFirstByDefault && input.value == null && props.options.length > 0) {
  if (props.groups) {
    input.value = props.options[0].options[0]?.value
  } else {
    input.value = props.options[0].value
  }
}

const attrs = useComputedAttrs(props, props.type)

const getCurrencySymbol = (code: string): string => {
  try {
    const parts = new Intl.NumberFormat('en', { style: 'currency', currency: code }).formatToParts(0)
    /* v8 ignore next - if the currency code is correct this will always yield a value -- @preserve */
    return parts.find((part) => part.type === 'currency')?.value ?? code
  } catch {
    return code
  }
}

type FormInputTextIcon = { type: 'text'; text: string }
type FormInputComponentIcon = { type: 'icon'; icon: Component }
type FormInputIcon = FormInputTextIcon | FormInputComponentIcon

const leftIcon = computed<FormInputIcon | null>(() => {
  if (props.type === 'money') return { type: 'text', text: getCurrencySymbol(props.currency ?? 'USD') }
  const icon = props.leftIcon ?? (props.type === 'search' ? IconSearch : null)
  return icon ? { type: 'icon', icon } : null
})
const rightIcon = computed<FormInputComponentIcon | null>(() => {
  const icon = props.rightIcon ?? (props.type === 'search' && input.value ? IconX : null)
  return icon ? { type: 'icon', icon } : null
})

const rightIconAttrs = computed(() => {
  if (props.type === 'search') {
    const resetValue = () => {
      /* v8 ignore next - search inputs don't use number modelValue in practice -- @preserve */
      input.value = typeof props.modelValue === 'number' ? undefined : ''
      emit('reset')
    }
    if (props.disabled) {
      return {
        role: 'button',
        'aria-label': 'Clear',
        'aria-disabled': true,
        tabIndex: -1,
      }
    }
    return {
      role: 'button',
      tabIndex: 0,
      'aria-label': 'Clear',
      onClick: resetValue,
      onKeydown: (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          resetValue()
        }
      },
    }
  }
  return null
})

const errorInternal = ref<string | boolean | null>(null)

const uploadFileName = computed(() => {
  const val = props.modelValue
  return val instanceof File ? val.name : undefined
})

function onChildValidation(message: string | null) {
  errorInternal.value = message
}

const validate = () => {
  const inputElements = inputEls.value
  /* v8 ignore next - Defensive code, not to cover a known case, we cannot add coverage -- @preserve */
  if (!inputElements || inputElements.length === 0) return

  for (const inputElement of inputElements) {
    const error = inputElement.validity.valid || props.disabled ? null : inputElement.validationMessage
    errorInternal.value = error
    emit('validation', error, inputElement)
    if (error) break
  }
}

function onNativeChange(e?: Event) {
  validate()
  emit('change', e)
}

function onNativeBlur() {
  validate()
  emit('blur')
}

const errorMessage = computed(() => {
  if (typeof props.error === 'string') return props.error
  if (errorInternal.value) return errorInternal.value
  return ''
})

const invalidInput = computed(() => props.error === true || Boolean(errorMessage.value))

const characterCountState = computed(() => {
  if ((props.type === 'string' || props.type === 'text') && props.showCharacterCount && props.maxlength != null) {
    const value = props.modelValue || ''
    return { count: value.length, max: props.maxlength }
  }
  return null
})

const footerDescription = computed(() => (hasLabelAbove.value ? props.description : undefined))

onMounted(() => {
  inputEls.value = Array.from(getCurrentInstance()?.proxy?.$el.querySelectorAll('input, select, textarea'))
})

const normalizedType = computed(() => {
  if (props.type === 'money') return 'number'
  const inputTypes = ['text', 'email', 'password', 'number', 'tel', 'url']
  return inputTypes.includes(props.type) ? props.type : 'text'
})

const validateAndReturnError = () => {
  validate()
  return errorInternal.value
}

defineExpose<FormInputExposed>({ validateAndReturnError })

const typeToComponentMap: Partial<Record<FormInputType, Component>> = {
  select: SelectField,
  country: CountryField,
  checkbox: CheckboxField,
  boolean: BooleanSwitch,
  date: DateField,
  'radio-group': RadioGroupField,
  'radio-group-vertical': RadioGroupVerticalField,
  tel: PhoneNumberField,
  text: TextareaField,
  passwordViewable: PasswordViewableField,
  upload: DropzoneField,
}

const componentProps = computed(() => {
  if (props.type === 'upload') {
    return {
      maxImageWidth: props.imageUploadMaxWidth,
      maxImageHeight: props.imageUploadMaxHeight,
      thumbnail: props.imageUploadThumbnail,
      name: attrs.value.name,
      id: attrs.value.id,
      readOnly: props.readOnly,
      disabled: props.disabled,
      error: typeof props.error === 'string' ? props.error : undefined,
    }
  }
  const result: FormInputProps & { indeterminate?: boolean } = { ...props }
  if (props.type === 'checkbox') {
    result.indeterminate = Boolean(props.indeterminate)
  }
  return result
})

const componentEvents = computed(() => {
  if (props.type === 'upload') {
    return { change: (e?: Event) => emit('change', e) }
  } else {
    return {
      validation: onChildValidation,
      change: (e?: Event) => {
        validate()
        emit('change', e)
      },
      blur: () => {
        validate()
        emit('blur')
      },
      validate: validate,
      invalid: (e?: Event) => {
        e?.preventDefault()
        validate()
      },
    }
  }
})

function onWheel(e: WheelEvent) {
  /* v8 ignore next - non-number inputs never increment on scroll, guard is defensive -- @preserve */
  if (normalizedType.value !== 'number') return
  ;(e.target as HTMLInputElement).blur()
}

const designSystemDefaults = inject(DESIGN_SYSTEM_DEFAULTS, null)
const inputSize = computed(() => {
  if (props.inputSize) return props.inputSize
  if (props.type === 'checkbox' && designSystemDefaults?.formInput?.checkboxSize)
    return designSystemDefaults.formInput.checkboxSize
  return designSystemDefaults?.formInput?.defaultSize ?? 'md'
})

const componentPropsWithAttrs = computed(() => ({
  ...componentProps.value,
  ...getCurrentInstance()?.proxy?.$attrs,
}))
</script>

<template>
  <div
    class="form-input"
    :class="[
      props.class,
      `size-${inputSize}`,
      {
        error: invalidInput,
        disabled: props.disabled,
        [props.type]: true,
        selected: input && !hasLabelAbove,
      },
    ]"
  >
    <slot name="before" />

    <label
      v-if="hasLabelAbove && props.label"
      :for="props.id"
      :class="['label', 'group-label', { 'visually-hidden': props.hideLabel }]"
    >
      {{ props.label }}
      <small v-if="!props.disabled && !props.required && props.showOptionalLabel" class="optional"> (optional) </small>
      <span v-else-if="!props.disabled && props.required && !props.hideRequiredLabel" class="required"> * </span>
    </label>

    <div class="input-wrapper">
      <template v-if="props.readOnly && props.type === 'upload'">
        <div class="preview">
          <template v-if="props.imageUploadThumbnail || uploadFileName">
            <img v-if="props.imageUploadThumbnail" :src="props.imageUploadThumbnail" alt="" />
            <span v-if="uploadFileName" class="upload-filename">{{ uploadFileName }}</span>
          </template>
          <span v-else class="upload-empty">No file selected</span>
        </div>
      </template>

      <template v-else-if="typeToComponentMap[props.type] !== undefined">
        <component
          :is="typeToComponentMap[props.type]"
          v-bind="componentPropsWithAttrs"
          v-model="input"
          v-on="componentEvents"
        />
        <label v-if="!hasLabelAbove && props.label" :for="props.id" class="label">
          {{ props.label }}
          <small v-if="!props.disabled && !props.required && props.showOptionalLabel" class="optional">
            (optional)
          </small>
          <span v-else-if="!props.disabled && props.required && !props.hideRequiredLabel" class="required"> * </span>
          <div v-if="props.description" class="description" v-shtml="props.description" />
        </label>
      </template>

      <template v-else>
        <div v-if="leftIcon" class="left-icon" :class="{ 'left-currency': leftIcon.type === 'text' }">
          <BaseIcon v-if="leftIcon.type === 'icon'" class="input-icon" :icon="leftIcon.icon" />
          <template v-else>{{ leftIcon.text }}</template>
        </div>
        <input
          class="input"
          v-model="input"
          v-autofocus="props.autofocus"
          v-bind="attrs"
          :type="normalizedType"
          :aria-required="props.required"
          :aria-invalid="invalidInput"
          :aria-describedby="uniqueId"
          @blur="onNativeBlur"
          @validate="validate"
          @change="onNativeChange"
          @invalid.prevent="validate"
          @wheel="onWheel"
          :tabindex="props.tabindex"
        />
        <div v-if="rightIcon" class="right-icon" v-bind="rightIconAttrs">
          <BaseIcon class="input-icon" :icon="rightIcon.icon" />
        </div>
      </template>
    </div>
    <div v-if="characterCountState" class="field-footer">
      <div v-if="footerDescription" class="description" v-shtml="footerDescription" />
      <span class="character-count"> {{ characterCountState.count }} / {{ characterCountState.max }} </span>
    </div>
    <div v-else-if="footerDescription" class="description" v-shtml="footerDescription" />

    <span v-if="errorMessage" role="alert" :id="uniqueId" class="error-message" v-shtml="errorMessage" />
  </div>
</template>

<!-- Unscoped: .form-input styles need to apply to child components as well. -->
<style>
.form-input {
  font-size: var(--_input-font-size);
  --_input-font-size: var(--input-font-size-md);
  --_input-height: 3em;
  --_toggle-height: 1.5em;
  --_toggle-width: 2.5em;
  --_boolean-switch-height: 3em;
  --_input-icon-size: var(--touch-target-height);

  --ms-font-size: var(--_input-font-size);
  --ms-option-font-size: var(--_input-font-size);

  &.size-xs {
    --_input-font-size: var(--input-font-size-xs);
  }

  &.size-sm {
    --_input-font-size: var(--input-font-size-sm);
  }

  &.size-lg {
    --_input-font-size: var(--input-font-size-lg);
  }

  &.size-xl {
    --_input-font-size: var(--input-font-size-xl);
  }

  display: inline-flex;
  flex-direction: column;
  align-items: start;
  width: 100%;

  .input,
  textarea {
    background: var(--c-input-background);
    border: 1px solid var(--form-input-border);
    border-radius: var(--form-input-border-radius);
    width: 100%;
    font-family: var(--font-family-body);
    font-size: var(--_input-font-size);
    font-style: normal;
    font-weight: var(--font-weight-regular);
    line-height: 1.25em;
    color: var(--c-input-text);

    &::placeholder {
      color: var(--c-input-disabled-text);
    }

    &:disabled {
      background: var(--c-input-disabled-background);
      color: var(--c-input-disabled-text);
    }

    &:hover:not(:disabled) {
      border-color: var(--form-input-border-hover);
    }

    &:focus {
      outline: none;

      &,
      &:hover {
        border-color: var(--c-input-border-focus);
      }
    }

    &:focus-visible {
      outline: none;
      border-color: var(--c-input-border-focus);
      box-shadow: var(--c-input-focus-visible);
    }
  }

  .input {
    height: var(--_input-height);
    padding: 0 0 0 0.875em;
  }

  .multiselect {
    min-height: var(--_input-height);
    font-size: var(--_input-font-size);
  }

  input.multiselect-search {
    font-size: var(--_input-font-size);
  }

  .label {
    width: 100%;
    font-family: var(--font-family-body);
    font-size: var(--font-size-body);
    font-weight: var(--form-label-font-weight);
    line-height: var(--line-height-body);
    font-style: normal;
    color: var(--c-input-text);

    &.group-label {
      font-weight: var(--form-label-font-weight);
      margin-bottom: 0.3125em;
    }
  }

  .error-message {
    display: block;
    width: 100%;
    margin: 0;
    padding: 0.4375rem 0 0;
    font-size: var(--font-size-body);
    font-weight: var(--font-weight-regular);
    color: var(--c-input-border-error);
  }

  &.text,
  &.upload {
    .input-wrapper {
      max-height: none;
    }
  }

  &.upload .preview {
    display: flex;
    flex-direction: column;
    gap: 0.3125em;

    img {
      max-width: 100%;
      display: flex;
      max-height: 21.875em;
    }

    .upload-filename,
    .upload-empty {
      font-size: var(--font-size-body);
      color: var(--text-tertiary);
    }
  }

  .input-wrapper,
  .description {
    position: relative;
    width: 100%;
    color: var(--c-input-text);
    font-weight: var(--font-weight-regular);
  }

  .description {
    margin-top: 0.3125em;
    font-family: var(--font-family-body);
    font-size: var(--font-size-body-small);
    line-height: var(--line-height-body);
    color: var(--text-tertiary);
  }

  .field-footer {
    display: flex;
    width: 100%;
    align-items: flex-start;
    gap: 0.5em;
    margin-top: 0.3125em;
    font-family: var(--font-family-body);
    font-size: var(--font-size-body);
    line-height: var(--line-height-body);
    color: var(--text-tertiary);
    font-weight: var(--font-weight-regular);

    .description {
      flex: 1;
      min-width: 0;
      width: auto;
      margin-top: 0;
    }
  }

  .character-count {
    margin-left: auto;
    flex-shrink: 0;
    white-space: nowrap;
  }

  .input-wrapper {
    display: flex;
  }

  .optional {
    color: var(--c-input-text);
    font-weight: var(--font-weight-regular);
    line-height: normal;
  }

  .required {
    color: var(--c-input-border-error);
    font-size: var(--input-required-indicator-font-size);
    font-style: normal;
    height: 0.9375em;
    display: inline-block;
  }

  .left-icon,
  .right-icon {
    --_icon-color: var(--c-input-icon-color);
    position: absolute;
    top: 50%;
    margin-top: calc(var(--_input-icon-size) / -2);
    width: var(--_input-icon-size);
    height: var(--_input-icon-size);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--_icon-color);

    .input-icon {
      font-size: 13.333px;
    }
  }

  .left-icon {
    left: 0.25rem;

    &::after {
      content: '';
      position: absolute;
      right: -0.25rem;
      top: 50%;
      transform: translateY(-50%);
      width: 1px;
      height: 1.25rem;
      background: var(--form-input-border);
    }
  }

  .left-currency {
    font-size: var(--_input-font-size);
    color: var(--c-input-text);
  }

  .right-icon {
    right: 0.5rem;
    cursor: pointer;
  }

  .right-icon,
  .left-icon {
    padding: 12px;
  }

  &.search {
    .left-icon {
      padding: 8px;
    }

    .right-icon {
      padding: 15px;
    }
  }

  &.disabled .right-icon {
    pointer-events: none;
    cursor: default;
  }

  &:has(.left-icon) .input {
    padding-left: calc(var(--_input-icon-size) + 0.25rem + 1rem);
  }

  &:has(.right-icon) .input {
    padding-right: calc(var(--_input-icon-size) + 0.25rem + 1rem);
  }

  textarea {
    padding: 0.875em;
  }

  &.error {
    .error-message {
      color: var(--c-input-border-error);
      display: block;
    }

    .input,
    input.multiselect-search,
    .multiselect,
    textarea {
      background: var(--error-background-color);

      &,
      &:hover {
        border-color: var(--c-input-border-error);
      }
    }
  }

  &.boolean {
    .input-wrapper {
      display: flex;
      border: 1px solid var(--c-boolean-switch-border);
      border-radius: var(--radius-small);
      width: 256px;
    }
  }

  &.radio-group-vertical {
    .input-wrapper {
      flex-direction: column;
      max-height: none;
      margin: 0;
      overflow: hidden;
      border-radius: var(--radius-small);
      background-color: var(--c-input-background);
      border: 1px solid var(--c-radio-group-vertical-separator);
    }
  }

  &.checkbox {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    width: auto;
    max-width: none;

    .input-wrapper {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      margin-top: 0;
      font-size: var(--_input-font-size);
    }

    .label {
      font-family: var(--font-family-body);
      font-size: var(--font-size-body);
      font-weight: var(--form-label-font-weight);
      line-height: var(--line-height-body);
      color: var(--c-input-text);
      cursor: pointer;
      margin-left: 0.625em;
    }

    &.disabled .label {
      cursor: default;
    }

    .description {
      max-height: none;
    }
  }

  &.checkbox {
    .input-wrapper {
      justify-content: flex-start;
      align-self: auto;
    }
  }

  &.date .input::-webkit-calendar-picker-indicator {
    height: 1.25em;
    width: 2.375em;
    cursor: pointer;
    border-left: 1px solid var(--form-input-border);
    background-position: center center;
    background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7.92188 10.625C8.08594 10.625 8.25 10.4883 8.25 10.2969V9.20312C8.25 9.03906 8.08594 8.875 7.92188 8.875H6.82812C6.63672 8.875 6.5 9.03906 6.5 9.20312V10.2969C6.5 10.4883 6.63672 10.625 6.82812 10.625H7.92188ZM10.875 10.2969V9.20312C10.875 9.03906 10.7109 8.875 10.5469 8.875H9.45312C9.26172 8.875 9.125 9.03906 9.125 9.20312V10.2969C9.125 10.4883 9.26172 10.625 9.45312 10.625H10.5469C10.7109 10.625 10.875 10.4883 10.875 10.2969ZM13.5 10.2969V9.20312C13.5 9.03906 13.3359 8.875 13.1719 8.875H12.0781C11.8867 8.875 11.75 9.03906 11.75 9.20312V10.2969C11.75 10.4883 11.8867 10.625 12.0781 10.625H13.1719C13.3359 10.625 13.5 10.4883 13.5 10.2969ZM10.875 12.9219V11.8281C10.875 11.6641 10.7109 11.5 10.5469 11.5H9.45312C9.26172 11.5 9.125 11.6641 9.125 11.8281V12.9219C9.125 13.1133 9.26172 13.25 9.45312 13.25H10.5469C10.7109 13.25 10.875 13.1133 10.875 12.9219ZM8.25 12.9219V11.8281C8.25 11.6641 8.08594 11.5 7.92188 11.5H6.82812C6.63672 11.5 6.5 11.6641 6.5 11.8281V12.9219C6.5 13.1133 6.63672 13.25 6.82812 13.25H7.92188C8.08594 13.25 8.25 13.1133 8.25 12.9219ZM13.5 12.9219V11.8281C13.5 11.6641 13.3359 11.5 13.1719 11.5H12.0781C11.8867 11.5 11.75 11.6641 11.75 11.8281V12.9219C11.75 13.1133 11.8867 13.25 12.0781 13.25H13.1719C13.3359 13.25 13.5 13.1133 13.5 12.9219ZM16.125 5.8125C16.125 5.10156 15.5234 4.5 14.8125 4.5H13.5V3.07812C13.5 2.91406 13.3359 2.75 13.1719 2.75H12.0781C11.8867 2.75 11.75 2.91406 11.75 3.07812V4.5H8.25V3.07812C8.25 2.91406 8.08594 2.75 7.92188 2.75H6.82812C6.63672 2.75 6.5 2.91406 6.5 3.07812V4.5H5.1875C4.44922 4.5 3.875 5.10156 3.875 5.8125V15.4375C3.875 16.1758 4.44922 16.75 5.1875 16.75H14.8125C15.5234 16.75 16.125 16.1758 16.125 15.4375V5.8125ZM14.8125 15.2734C14.8125 15.3828 14.7305 15.4375 14.6484 15.4375H5.35156C5.24219 15.4375 5.1875 15.3828 5.1875 15.2734V7.125H14.8125V15.2734Z' fill='%23282B37'/%3E%3C/svg%3E");
  }

  /* WebKit/Blink only; Firefox and others keep default number stepper. */
  &.number .input {
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      margin-right: 0.5rem;
    }
  }
}
</style>
